import { isTauri } from '../utils';

import { RPA_RECORDER_PROTOCOL } from './protocol';
import {
  RecorderUploaderError,
  type RecorderAnalysisStatusChangedPayload,
  type RecorderAnalysisStatusListener,
  type RecorderPerformUploadOutcome,
  type RecorderUploadAuthContext,
  type RecorderUploadOutcome,
  type RecorderUploadPreparation,
  type RecorderUploadPreparationResult,
  type RecorderUploadProgressListener,
  type RecorderUploadProgressPayload,
  type RecorderUploadSidecar,
  type RecorderUploadStateChangedPayload,
  type RecorderUploadStateListener,
  type RecorderUploadUnsubscribe,
  type WorkflowWirePayload,
} from './upload-types';

const COMMAND_BY_OUTCOME_KIND: Record<RecorderUploadOutcome['kind'], string> = {
  'analysis-ready': RPA_RECORDER_PROTOCOL.commands.markAnalysisReady,
  'analysis-failed': RPA_RECORDER_PROTOCOL.commands.markAnalysisFailed,
};

interface InvokeApi {
  invoke<T>(command: string, payload?: Record<string, unknown>): Promise<T>;
}

interface EventApi {
  listen<T>(
    eventName: string,
    handler: (event: { payload: T }) => void
  ): Promise<() => void>;
}

interface UploadBridgeDeps {
  isTauri: () => boolean;
  loadInvoke: () => Promise<InvokeApi>;
  loadEvent: () => Promise<EventApi>;
}

const defaultDeps: UploadBridgeDeps = {
  isTauri,
  loadInvoke: () => import('@tauri-apps/api/core'),
  loadEvent: () => import('@tauri-apps/api/event'),
};

export interface RecorderUploadBridge {
  isAvailable(): boolean;
  init(): Promise<void>;
  cleanup(): Promise<void>;
  prepareUpload(
    sessionId: string,
    name: string
  ): Promise<RecorderUploadPreparationResult>;
  retryUpload(sessionId: string): Promise<RecorderUploadPreparationResult>;
  performUpload(
    sessionId: string,
    name: string,
    auth: RecorderUploadAuthContext
  ): Promise<RecorderPerformUploadOutcome>;
  getUploadState(sessionId: string): Promise<RecorderUploadSidecar>;
  getWorkflow(sessionId: string): Promise<WorkflowWirePayload>;
  record(
    sessionId: string,
    outcome: RecorderUploadOutcome
  ): Promise<RecorderUploadSidecar>;
  subscribeUploadState(listener: RecorderUploadStateListener): RecorderUploadUnsubscribe;
  subscribeUploadProgress(
    listener: RecorderUploadProgressListener
  ): RecorderUploadUnsubscribe;
  subscribeAnalysisStatus(
    listener: RecorderAnalysisStatusListener
  ): RecorderUploadUnsubscribe;
}

class RecorderUploadBridgeImpl implements RecorderUploadBridge {
  private initPromise: Promise<void> | null = null;
  private unlistenUploadState: RecorderUploadUnsubscribe | null = null;
  private unlistenUploadProgress: RecorderUploadUnsubscribe | null = null;
  private unlistenAnalysisStatus: RecorderUploadUnsubscribe | null = null;
  private readonly uploadStateListeners = new Set<RecorderUploadStateListener>();
  private readonly uploadProgressListeners = new Set<RecorderUploadProgressListener>();
  private readonly analysisStatusListeners = new Set<RecorderAnalysisStatusListener>();
  private invokeApiCache: Promise<InvokeApi> | null = null;
  private eventApiCache: Promise<EventApi> | null = null;

  constructor(private readonly deps: UploadBridgeDeps = defaultDeps) {}

  private getInvokeApi(): Promise<InvokeApi> {
    return (this.invokeApiCache ??= this.deps.loadInvoke());
  }

  private getEventApi(): Promise<EventApi> {
    return (this.eventApiCache ??= this.deps.loadEvent());
  }

  isAvailable(): boolean {
    return this.deps.isTauri();
  }

  init(): Promise<void> {
    if (!this.deps.isTauri()) return Promise.resolve();
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.attachListeners();
    return this.initPromise;
  }

  private async attachListeners(): Promise<void> {
    const eventApi = await this.getEventApi();
    this.unlistenUploadState = await eventApi.listen<RecorderUploadStateChangedPayload>(
      RPA_RECORDER_PROTOCOL.events.uploadStateChanged,
      (event) => {
        for (const listener of this.uploadStateListeners) listener(event.payload);
      }
    );
    this.unlistenUploadProgress = await eventApi.listen<RecorderUploadProgressPayload>(
      RPA_RECORDER_PROTOCOL.events.uploadProgress,
      (event) => {
        for (const listener of this.uploadProgressListeners) listener(event.payload);
      }
    );
    this.unlistenAnalysisStatus =
      await eventApi.listen<RecorderAnalysisStatusChangedPayload>(
        RPA_RECORDER_PROTOCOL.events.analysisStatusChanged,
        (event) => {
          for (const listener of this.analysisStatusListeners) listener(event.payload);
        }
      );
  }

  cleanup(): Promise<void> {
    this.initPromise = null;
    this.unlistenUploadState?.();
    this.unlistenUploadProgress?.();
    this.unlistenAnalysisStatus?.();
    this.unlistenUploadState = null;
    this.unlistenUploadProgress = null;
    this.unlistenAnalysisStatus = null;
    this.uploadStateListeners.clear();
    this.uploadProgressListeners.clear();
    this.analysisStatusListeners.clear();
    return Promise.resolve();
  }

  async prepareUpload(
    sessionId: string,
    name: string
  ): Promise<RecorderUploadPreparationResult> {
    try {
      const invoke = await this.getInvokeApi();
      const preparation = await invoke.invoke<RecorderUploadPreparation>(
        RPA_RECORDER_PROTOCOL.commands.prepareUpload,
        { sessionId, name }
      );
      return { ok: true, preparation };
    } catch (raw) {
      return { ok: false, error: uploaderErrorFromUnknown(raw) };
    }
  }

  async retryUpload(sessionId: string): Promise<RecorderUploadPreparationResult> {
    try {
      const invoke = await this.getInvokeApi();
      const preparation = await invoke.invoke<RecorderUploadPreparation>(
        RPA_RECORDER_PROTOCOL.commands.retryUpload,
        { sessionId }
      );
      return { ok: true, preparation };
    } catch (raw) {
      return { ok: false, error: uploaderErrorFromUnknown(raw) };
    }
  }

  async performUpload(
    sessionId: string,
    name: string,
    auth: RecorderUploadAuthContext
  ): Promise<RecorderPerformUploadOutcome> {
    try {
      const invoke = await this.getInvokeApi();
      return await invoke.invoke<RecorderPerformUploadOutcome>(
        RPA_RECORDER_PROTOCOL.commands.performUpload,
        { sessionId, name, auth }
      );
    } catch (raw) {
      throw uploaderErrorFromUnknown(raw);
    }
  }

  async getUploadState(sessionId: string): Promise<RecorderUploadSidecar> {
    try {
      const invoke = await this.getInvokeApi();
      return await invoke.invoke<RecorderUploadSidecar>(
        RPA_RECORDER_PROTOCOL.commands.getUploadState,
        { sessionId }
      );
    } catch (raw) {
      throw uploaderErrorFromUnknown(raw);
    }
  }

  async getWorkflow(sessionId: string): Promise<WorkflowWirePayload> {
    try {
      const invoke = await this.getInvokeApi();
      return await invoke.invoke<WorkflowWirePayload>(
        RPA_RECORDER_PROTOCOL.commands.getWorkflow,
        {
          sessionId,
        }
      );
    } catch (raw) {
      throw uploaderErrorFromUnknown(raw);
    }
  }

  async record(
    sessionId: string,
    outcome: RecorderUploadOutcome
  ): Promise<RecorderUploadSidecar> {
    const { kind, ...rest } = outcome;
    try {
      const invoke = await this.getInvokeApi();
      return await invoke.invoke<RecorderUploadSidecar>(COMMAND_BY_OUTCOME_KIND[kind], {
        sessionId,
        ...rest,
      });
    } catch (raw) {
      throw uploaderErrorFromUnknown(raw);
    }
  }

  subscribeUploadState(listener: RecorderUploadStateListener): RecorderUploadUnsubscribe {
    this.uploadStateListeners.add(listener);
    return () => this.uploadStateListeners.delete(listener);
  }

  subscribeUploadProgress(
    listener: RecorderUploadProgressListener
  ): RecorderUploadUnsubscribe {
    this.uploadProgressListeners.add(listener);
    return () => this.uploadProgressListeners.delete(listener);
  }

  subscribeAnalysisStatus(
    listener: RecorderAnalysisStatusListener
  ): RecorderUploadUnsubscribe {
    this.analysisStatusListeners.add(listener);
    return () => this.analysisStatusListeners.delete(listener);
  }
}

function uploaderErrorFromUnknown(raw: unknown): RecorderUploaderError {
  if (raw instanceof RecorderUploaderError) return raw;
  if (typeof raw === 'object' && raw !== null) {
    const payload = raw as { error_code?: unknown; code?: unknown };
    const code = payload.error_code ?? payload.code;
    if (typeof code === 'string') {
      return new RecorderUploaderError(uploaderErrorCodeFromString(code), { cause: raw });
    }
  }
  if (raw instanceof Error) return new RecorderUploaderError('UNKNOWN', { cause: raw });
  return new RecorderUploaderError('UNKNOWN', { cause: raw });
}

function uploaderErrorCodeFromString(
  code: string
): ConstructorParameters<typeof RecorderUploaderError>[0] {
  switch (code) {
    case 'UPLOAD_AUTH_UNAVAILABLE':
    case 'UPLOAD_SESSION_NOT_FOUND':
    case 'UPLOAD_STATE_NOT_FOUND':
    case 'UPLOAD_INVALID_STAGE':
    case 'UPLOAD_ARCHIVE_FAILED':
    case 'UPLOAD_NETWORK_ERROR':
    case 'UPLOAD_SERVER_ERROR':
    case 'UPLOAD_CLIENT_ERROR':
    case 'UPLOAD_FORBIDDEN':
    case 'UPLOAD_INTERRUPTED':
    case 'WORKFLOW_NOT_READY':
    case 'WORKFLOW_NOT_FOUND':
    case 'WORKFLOW_WRITE_FAILED':
    case 'GENERATION_FAILED':
    case 'UNKNOWN':
      return code;
    default:
      return 'UNKNOWN';
  }
}

export function createRecorderUploadBridge(
  deps: UploadBridgeDeps = defaultDeps
): RecorderUploadBridge {
  return new RecorderUploadBridgeImpl(deps);
}

export const recorderUploadBridge: RecorderUploadBridge = createRecorderUploadBridge();
